import {
  autoPlacement,
  autoUpdate,
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useTransitionStyles,
  useTypeahead,
} from "@floating-ui/react";

import React, {
  type ChangeEvent,
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import type {
  ClassAttributes,
  CSSProperties,
  FocusEvent,
  InputHTMLAttributes,
  MouseEventHandler,
  MutableRefObject,
  ReactNode,
} from "react";
import type {
  FontSizeProps,
  FontWeightProps,
  HeightProps,
  LineHeightProps,
  MaxHeightProps,
  MaxWidthProps,
  SpaceProps,
  WidthProps,
} from "styled-system";

import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";

import { css, type Interpolation } from "@emotion/react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";

import type { ExactTheme } from "src/lib/emotion/theme.ts";
import * as styles from "./Select.style.ts";
import type { ReactElement, Ref } from "react";

import type { PropsWithChildren } from "react";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";

import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { useControlled } from "@exonn/hooks";

import { SearchBar } from "src/components/Select/SearchBar.tsx";
import { EndAdornment } from "src/components/Select/EndAdornment.tsx";
import { Arrow } from "src/components/Select/Arrow.tsx";
import { Label } from "src/components/Select/Label.tsx";
import { Preview } from "src/components/Select/Preview.tsx";
import { ClearButton } from "src/components/Select/ClearButton.tsx";
import { Options } from "src/components/Select/Options.tsx";
import { StartAdornment } from "src/components/Select/StartAdornment.tsx";
import { isEmptyValue } from "src/components/Select/isEmptyValue.ts";

import { unique } from "@exonn/utils";
import { flushSync } from "react-dom";
import { Flex } from "src/components/Flex.tsx";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { SelectButtons } from "src/components/Select/SelectButtons.tsx";
import { IconButton2 } from "src/components/IconButton2/IconButton2.tsx";
import { useTranslation } from "react-i18next";

await loadTranslations(TranslationNamespaceEnum.SelectComponent);

export interface Option<Value = unknown> {
  value: Value;
  label: string;
  disabled?: boolean;
  renderOption?: (props: Omit<Option<Value>, "renderOption">) => ReactNode;
}

export interface GroupOption<Value = unknown> {
  value: Value;
  label: string;
  disabled?: boolean;
  options: Option<NoInfer<Value>>[];
}
export type MultipleOnChangeCallback<Value = unknown> = (
  value: Value[] | "",
) => void;

type MultipleProps<Value = unknown> = {
  isMultiple: true;
  onChange?: MultipleOnChangeCallback<Value>;
  defaultValue?: Value[] | "";
  value?: Value[] | "";
  options?: Option<NoInfer<Value>>[] | GroupOption<NoInfer<Value>>[];
  fallbackOptions?: Option<NoInfer<Value>>[];
  truncateOptions?: boolean;
};

export type SingleOnChangeCallback<Value = unknown> = (
  value: Value | "",
) => void;

type SingleProps<Value = unknown> = {
  isMultiple?: false;
  onChange?: SingleOnChangeCallback<Value>;
  defaultValue?: Value;
  value?: Value | "";
  options?: Option<NoInfer<Value>>[] | GroupOption<NoInfer<Value>>[];
  fallbackOptions?: Option<NoInfer<Value>>[];
};

export type SelectApi<Value = unknown> = {
  setActiveIndex: (index: number | null) => void;
  select: (option: Option<Value>) => void;
};

type CommonProps<Value = unknown> = {
  style?: CSSProperties;
  isLoading?: boolean;
  additionalLabelElems?: ReactNode[];
  label?: ReactNode;
  helperText?: ReactNode;
  required?: boolean;
  error?: string | boolean;
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  isLoadingData?: boolean;
  showSearch?: boolean;
  showAdvancedSearch?: boolean;
  onAdvancedSearchOpen?: (isModalOpen: boolean) => void;
  defaultSearchValue?: string;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchValueChange?: (search: string) => void;
  onClick?: MouseEventHandler;
  onBlur?: (e: FocusEvent) => void;
  onOpenChange?: (isOpen: boolean) => void;
  placeholder?: string;
  allowClearValue?: boolean;
  renderInPortal?: boolean;
  selectContainerStyle?: CSSProperties;
  renderOptionEstimateSize?: number;
  floatingError?: boolean;
  endAdornment?: ReactNode;
  startAdornment?: ReactNode;
  onScrollEnd?: () => void;
  isLoadingMoreOptions?: boolean;
  isLoadingSelectedOption?: boolean;
  optionsMinWidth?: number;
  /**
   * Стили выпадающего списка. Нужны там, где список — часть фирменного
   * рисунка экрана, а не служебное меню: отступы карточки, скруглённые
   * строки, своя подсветка выбранного.
   */
  optionsCss?: Interpolation<ExactTheme>;
  isButton?: boolean;
  maxOptions?: number;
  showButtons?: ("view" | "copy")[];
  additionalButtons?: ReactNode[];
  onViewButton?: () => void;
  getCopyText?: () => void;
  apiRef?: MutableRefObject<SelectApi<Value> | undefined>;
  onSearchBarEnter?: (options: Option<Value>[]) => void;
  customHeaderContent?: ReactNode;
};

type SelectBaseProps<Value = unknown> = (
  | MultipleProps<Value>
  | SingleProps<Value>
) &
  CommonProps<Value>;

export type SelectProps<Value = unknown> = SelectBaseProps<Value> &
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    | "onSelect"
    | "onClick"
    | "value"
    | "defaultValue"
    | "onBlur"
    | "width"
    | "height"
    | "onChange"
  > &
  Omit<ClassAttributes<HTMLElement>, "ref"> &
  SpaceProps &
  WidthProps &
  HeightProps &
  MaxWidthProps &
  MaxHeightProps &
  FontSizeProps &
  FontWeightProps &
  LineHeightProps;

const NoPortal = ({ children }: PropsWithChildren) => <div>{children}</div>;

const DEFAULT_FLOATING_MAX_HEIGHT = 400;
const DEFAULT_FLOATING_MIN_WIDTH = 200;

export const Select = forwardRef(function <Value = unknown>(
  props: SelectProps<Value>,
  ref: Ref<HTMLDivElement>,
) {
  const {
    renderInPortal = true,
    selectContainerStyle = {},
    label,
    style = {},
    onScrollEnd,
    floatingError = false,
    error,
    required,
    searchPlaceholder,
    fallbackOptions = [],
    placeholder,
    isLoading = false,
    customHeaderContent,
    isLoadingMoreOptions = false,
    helperText,
    allowClearValue = true,
    isLoadingSelectedOption = false,
    onClick: onReferenceClick,
    onBlur: onReferenceBlur,
    onFocus: onReferenceFocus,
    renderOptionEstimateSize,
    optionsMinWidth,
    optionsCss,
    options = [],
    name,
    id,
    isMultiple = false,
    isButton = false,
    searchValue: searchValueProp,
    defaultSearchValue = "",
    isLoadingData = false,
    onSearchValueChange,
    showSearch = false,
    showAdvancedSearch = false,
    onAdvancedSearchOpen,
    onChange,
    value: valueProp,
    defaultValue = "" as const,
    isOpen: isOpenProp,
    defaultIsOpen = false,
    onOpenChange: onOpenChangeProp,
    disabled = false,
    endAdornment,
    startAdornment,
    additionalLabelElems,
    additionalButtons,
    maxOptions = 15,
    showButtons,
    getCopyText,
    onViewButton,
    apiRef,
    onSearchBarEnter,
  } = props;
  // const { t } = useTranslation();
  // const placeholder =
  //   placeholderProp ??
  //   t(isMultiple ? "defaultMultiplePlaceholder" : "defaultPlaceholder", {
  //     ns: TranslationNamespaceEnum.SelectComponent,
  //   });

  useImperativeHandle(apiRef, () => ({
    setActiveIndex,
    select,
  }));

  const inputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation(TranslationNamespaceEnum.SelectComponent);

  const [maxHeight, setMaxHeight] = useState(DEFAULT_FLOATING_MAX_HEIGHT);

  const [value, setValueInternal] = useControlled({
    name: "Select",
    state: "value",
    controlled: valueProp,
    default: defaultValue,
  });

  const setValue = (value: Value | Value[] | "") => {
    setValueInternal(value);
    if (isMultiple) {
      (onChange as MultipleOnChangeCallback<Value> | undefined)?.(
        value as Value[],
      );
    } else {
      (onChange as SingleOnChangeCallback<Value> | undefined)?.(
        value as Value | "",
      );
    }
  };

  const [searchValue, setSearchValue] = useControlled({
    name: "Select",
    state: "searchValue",
    controlled: searchValueProp,
    default: defaultSearchValue,
    onChange: onSearchValueChange,
  });

  const [isOpen, setIsOpen] = useControlled({
    name: "Select",
    state: "isOpen",
    controlled: isOpenProp,
    default: defaultIsOpen,
    onChange: onOpenChangeProp,
  });

  const flatOptions: Option<Value>[] = options.flatMap((option) => {
    if ("options" in option) {
      return option.options ?? [];
    }
    return option;
  });

  const listRef = useRef<Array<HTMLElement | null>>([
    ...Array(flatOptions.length).fill(null),
  ]);

  const isTypingRef = useRef(false);

  const floating = useFloating({
    open: isOpen,
    onOpenChange: (isOpen) => {
      setIsOpen(isOpen);
      if (!isOpen) {
        setSearchValue("");
        // return focus to select when closing floating menu
        setTimeout(() => {
          (floating.refs.reference.current as HTMLElement)?.focus();
        });
      }
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(7),
      autoPlacement({
        padding: 10,
        allowedPlacements: [
          "top-start",
          "bottom-start",
          "top-end",
          "bottom-end",
        ],
      }),
      size({
        apply({ rects, elements, availableHeight }) {
          // Use state instead of directly mutating so that `react-virtual`
          // scrollToIndex() function works in the layout effect.
          flushSync(() => {
            setMaxHeight(availableHeight);
          });
          Object.assign(elements.floating.style, {
            //maxHeight: `${availableHeight}px`,
            width: `${Math.max(
              rects.reference.width,
              optionsMinWidth ?? DEFAULT_FLOATING_MIN_WIDTH,
            )}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const transitionStyles = useTransitionStyles(floating.context, {
    initial: {
      opacity: 0,
    },
    open: {
      opacity: 1,
    },
    close: {
      opacity: 0,
    },
  });

  const listWrapperRef = useRef<HTMLDivElement>(null);

  const valueIsEmpty = isEmptyValue({
    value,
    isMultiple: isMultiple,
  });

  const selectedIndex = getSelectedIndex({
    flatOptions,
    value,
    isMultiple,
  });

  const [activeIndex, setActiveIndex] = useState<number | null>(selectedIndex);

  const select = (item: Option<Value>) => {
    if (item.disabled) {
      return;
    }
    if (isMultiple) {
      const nextValue: Value[] = unique([
        ...(Array.isArray(value) ? value : []),
        item.value,
      ]);
      setValue(nextValue);
    } else {
      setIsOpen(false);
      setSearchValue("");
      const nextValue = item.value;
      setValue(nextValue);
    }
  };

  const disabledIndices = options.flatMap((option, index) =>
    option.disabled ? [index] : [],
  );
  const handleNavigate = (index: number | null) => {
    // for some reason index can be string
    const parsedIndex = index != null ? +index : index;
    setActiveIndex(parsedIndex);
  };
  const handleMatch = (index: number) => {
    if (isOpen) {
      setActiveIndex(index);
    } else {
      select(flatOptions[index]);
    }
  };
  const interactions = useInteractions([
    useClick(floating.context, {
      enabled: !disabled,
    }),
    useDismiss(floating.context),
    useListNavigation(floating.context, {
      listRef,
      activeIndex,
      selectedIndex,
      focusItemOnOpen: true,
      focusItemOnHover: true,
      loop: true,
      virtual: true,
      disabledIndices: disabledIndices,
      onNavigate: handleNavigate,
    }),
    useTypeahead(floating.context, {
      enabled: !showSearch,
      listRef: useRef(
        flatOptions.map((option) => (option.disabled ? null : option.label)),
      ),
      activeIndex,
      selectedIndex,
      onMatch: handleMatch,
      onTypingChange(isTyping) {
        isTypingRef.current = isTyping;
      },
    }),
  ]);

  const [pointer, setPointer] = useState(false);

  const handleReset = () => {
    setValue(isMultiple ? [] : "");
  };

  const handleSearchReset = () => {
    setSearchValue("");
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.currentTarget.value);
  };

  const PortalComponent = renderInPortal ? FloatingPortal : NoPortal;

  return (
    <Skeleton isLoading={isLoading} style={style}>
      <div
        css={styles.root({
          isButton,
        })}
        style={style}
        ref={ref}
      >
        <Label
          label={label}
          helperText={helperText}
          required={required}
          additionalLabelElems={additionalLabelElems}
        />

        <Flex
          style={{
            width: "100%",
          }}
          gap={"5px"}
        >
          <div
            css={[
              styles.container({
                isOpen: isOpen,
                hasError: !!error,
                isDisabled: disabled,
                isMultiple,
                isButton,
              }),
              css({
                ...selectContainerStyle,
              }),
            ]}
            {...interactions.getReferenceProps({
              tabIndex: disabled ? undefined : 0,
              onClick: onReferenceClick,
              onBlur: onReferenceBlur,
              onFocus: onReferenceFocus,
            })}
            ref={floating.refs.setReference}
          >
            <StartAdornment
              isButton={isButton}
              startAdornment={startAdornment}
              disabled={disabled}
            />

            <div css={styles.inner}>
              {!isMultiple && (
                <input
                  css={styles.input}
                  ref={inputRef}
                  id={id}
                  name={name}
                  readOnly={true}
                  value={value + ""}
                />
              )}
              <Preview<Value>
                truncateOptions={
                  props.isMultiple ? props?.truncateOptions : false
                }
                onOpen={(state) => setIsOpen(state)}
                isLoadingSelectedOption={isLoadingSelectedOption}
                fallbackOptions={fallbackOptions}
                value={value}
                disabled={disabled}
                flatOptions={flatOptions}
                isLoadingData={isLoadingData}
                placeholder={placeholder}
                isMultiple={isMultiple}
                onValueChange={setValue}
                isButton={isButton}
                isOpen={isOpen}
              />

              {allowClearValue && !disabled && !valueIsEmpty && !isButton && (
                <ClearButton onReset={handleReset} />
              )}

              <Arrow
                disabled={disabled}
                truncateOptions={
                  props.isMultiple ? props?.truncateOptions : false
                }
                isOpen={isOpen}
                isButton={isButton}
              />
            </div>
            <EndAdornment
              isButton={isButton}
              endAdornment={endAdornment}
              disabled={disabled}
            />
          </div>
          <SelectButtons
            getCopyText={getCopyText}
            showButtons={showButtons}
            onViewButton={onViewButton}
            additionalButtons={additionalButtons}
          />
        </Flex>

        {transitionStyles.isMounted && isOpen && (
          <PortalComponent id={FLOATING_PORTAL_ROOT_ID}>
            <FloatingFocusManager
              context={floating.context}
              modal={true}
              returnFocus={true}
            >
              <FloatingOverlay css={styles.floatingOverlay}>
                <div
                  css={[styles.floatingContainer, optionsCss]}
                  ref={floating.refs.setFloating}
                  tabIndex={-1}
                  {...interactions.getFloatingProps({
                    style: {
                      ...floating.floatingStyles,
                      ...transitionStyles.styles,
                      maxHeight,
                    },
                    onKeyDown(e) {
                      setPointer(false);

                      if (e.key === "Enter" && activeIndex !== null) {
                        select(flatOptions[activeIndex]);
                      }

                      if (e.key === " " && !isTypingRef.current) {
                        e.preventDefault();
                      }
                    },
                    onKeyUp(e) {
                      if (
                        e.key === " " &&
                        !isTypingRef.current &&
                        activeIndex !== null
                      ) {
                        select(flatOptions[activeIndex]);
                      }
                    },
                    onPointerMove() {
                      setPointer(true);
                    },
                  })}
                >
                  {customHeaderContent}
                  {showSearch && (
                    <Flex flexShrink={0} gap={10} css={styles.searchContainer}>
                      <Flex css={styles.searchInputContainer}>
                        <SearchBar
                          onChange={handleSearchChange}
                          onReset={handleSearchReset}
                          placeholder={searchPlaceholder}
                          searchValue={searchValue}
                          onArrowDown={() => {
                            const firstAvailableIndex = flatOptions.findIndex(
                              (_, index) => !disabledIndices.includes(index),
                            );
                            if (firstAvailableIndex !== -1) {
                              setActiveIndex(firstAvailableIndex);
                              listRef.current[firstAvailableIndex]?.focus();
                            }
                          }}
                          onEnter={() => {
                            if (flatOptions.length === 1) {
                              select(flatOptions[0]);
                            }
                            onSearchBarEnter?.(flatOptions);
                          }}
                        />
                      </Flex>
                      {showAdvancedSearch && (
                        <div
                          css={styles.advancedSearchButton}
                          onClick={() => {
                            onAdvancedSearchOpen?.(true);
                            setIsOpen(false);
                          }}
                        >
                          <IconButton2
                            helperText={t("advancedSearchHelperText")}
                            skin={"info"}
                          >
                            <i className={"icon-15-search_advn"} />
                          </IconButton2>
                        </div>
                      )}
                    </Flex>
                  )}

                  <LoadingOverlay
                    style={{
                      marginTop: showSearch ? 59 : 0,
                    }}
                    isLoading={isLoadingData}
                  >
                    <LoadingOverlay.Spinner width={40} height={40} />
                  </LoadingOverlay>
                  <Options<Value>
                    listWrapperRef={listWrapperRef}
                    floating={floating}
                    isTypingRef={isTypingRef}
                    searchValue={searchValue}
                    onSelect={select}
                    isMultiple={isMultiple}
                    isOpen={isOpen}
                    value={value}
                    onPointerChange={setPointer}
                    pointer={pointer}
                    selectedIndex={selectedIndex}
                    activeIndex={activeIndex}
                    interactions={interactions}
                    flatOptions={flatOptions}
                    options={options}
                    isLoadingData={isLoadingData}
                    isLoadingMoreOptions={isLoadingMoreOptions}
                    onScrollEnd={onScrollEnd}
                    renderOptionEstimateSize={renderOptionEstimateSize}
                    listRef={listRef}
                    maxOptions={maxOptions}
                  />
                </div>
              </FloatingOverlay>
            </FloatingFocusManager>
          </PortalComponent>
        )}
        {typeof error !== "boolean" && !!error && (
          <InputErrorMessage floating={floatingError} message={error} />
        )}
      </div>
    </Skeleton>
  );
}) as <Value = unknown>(
  p: SelectProps<Value> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;

const getSelectedIndex = ({
  isMultiple,
  value,
  flatOptions,
}: {
  isMultiple: boolean;
  value: any;
  flatOptions: Option<any>[];
}) => {
  const valueIsEmpty = isEmptyValue({
    value,
    isMultiple: isMultiple,
  });
  if (valueIsEmpty) {
    return null;
  }
  if (isMultiple) {
    if (!Array.isArray(value)) {
      return null;
    }
    return flatOptions.findIndex(
      (option) => option.value === value[value.length - 1],
    );
  } else {
    return flatOptions.findIndex((option) => option.value === value);
  }
};
